import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import MainLayout from "./Mainlayout";
import withAuthentication from "../hoc/withAuthentication";
import { useCurrentTalentProfile } from "../hooks";

const RouteLoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "40vh",
    }}
  >
    <CircularProgress />
  </Box>
);

const TalentHomepage = lazy(() =>
  import("../components/talent/TalentHomepage/TalentHomepage").then((m) => ({
    default: m.TalentHomepage,
  })),
);

const SignIn = lazy(() => import("../pages/Auth/SignIn_updated"));
const Register = lazy(() => import("../pages/Register/Register"));

const ForgotPasswordPage = lazy(() => import("../pages/Auth/ForgotPassword"));
const VerifyEmailPage = lazy(() => import("../pages/Auth/VerifyPage"));
const PasswordResetPage = lazy(() => import("../pages/Auth/ResetPassword"));

const Homepage = lazy(() => import("../pages/LandingPage/Home"));

const TalentNotification = lazy(() => import("./TalentNotification"));


const ContactPage = lazy(
  () => import("../pages/LandingPage/contacts/Contactpage"),
);

const SupportPage = lazy(() => import("../components/Support/SupportPage"));

const TalentWalletDashboardNew = lazy(
  () => import("../components/Wallet/Talent/TalentWalletDashboardNew"),
);
const ComingSoon = lazy(() => import("../pages/LandingPage/ComingSoon"));

const ChatWithAI = lazy(() => import("../components/AITwins/ChatWithAI"));
const AITwinPaymentPage = lazy(
  () => import("../components/TaasStudio/components/AITwinPaymentPage"),
);
const AITwinEnablePage = lazy(
  () => import("../components/TaasStudio/components/AITwinEnablePage"),
);
const AITwinConversationList = lazy(
  () => import("../components/TaasStudio/components/AITwinConversationList"),
);
const AITwinConversationView = lazy(
  () => import("../components/TaasStudio/components/AITwinConversationView"),
);
const AITwinSettingsPage = lazy(
  () =>
    import(
      "../components/TaasStudio/components/AITwin/components/AITwinSettingsPage"
    ),
);
const AITwinAnalyticsPage = lazy(
  () =>
    import(
      "../components/TaasStudio/components/AITwin/pages/AITwinAnalyticsPage"
    ),
);
const AITwinMonitorPage = lazy(
  () =>
    import("../components/TaasStudio/components/AITwin/pages/AITwinMonitorPage"),
);


const TalentWorkspacePage = lazy(
  () => import("../components/TaasStudio/TaasStudio"),
);


const InstantCardListTalent = lazy(
  () => import("../components/InstantCard/Talent/InstantCardListTalent"),
);


const AirbankPage = lazy(
  () => import("../components/airbank/pages/AirbankPage"),
);

const AirbankRegistrationPage = lazy(
  () => import("../pages/AirbankRegistrationPage"),
);
const AirbankStatusPage = lazy(
  () => import("../pages/AirbankStatusPage"),
);
const AirbankManagePage = lazy(
  () => import("../pages/AirbankManagePage"),
);
const AirbankEditPage = lazy(() => import("../pages/AirbankEditPage"));
const AirbankDnaImageUsageLogsPage = lazy(
  () => import("../pages/AirbankDnaImageUsageLogsPage"),
);
const ProtectedRegistrationRoute = lazy(() =>
  import(
    "../components/airbank/talent/AirbankRegistration/components/ProtectedRegistrationRoute"
  ).then((m) => ({ default: m.ProtectedRegistrationRoute })),
);
const AITwinListPage = lazy(() => import("../pages/AITwinListPage"));
const AiTDetailsPage = lazy(() => import("../pages/AiTDetailsPage"));



const CardWithTalent_AITwinPage = lazy(
  () => import("../pages/CardWithTalent_AITwinPage"),
);
const EditAitEnabledCardPage = lazy(
  () => import("../pages/EditAitEnabledCardPage"),
);
const TalentAitCardRequestsPage = lazy(
  () => import("../pages/TalentAitCardRequestsPage"),
);


const AuthenticatedLayout = withAuthentication(MainLayout);

const TalentRoutes = () => {
  function TalentEditRedirect() {
    const { currentProfile } = useCurrentTalentProfile();
    if (!currentProfile) return null;
    return (
      <Navigate to={`/talent/profile/edit/${currentProfile.id}`} replace />
    );
  }
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/signup/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password?/OTP" element={<VerifyEmailPage />} />
        {/* <Route path="cards/:id" element={<TalentCardDetailsPageWrapper />} /> */}
        
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/home" element={<Homepage />} />
        <Route
          path="/forgot-password/reset-password"
          element={<PasswordResetPage />}
        />
        <Route path="/" element={<AuthenticatedLayout />}>
          <Route path="dashboard" element={<TalentHomepage />} />
          <Route
            path="my-deck"
            element={<Navigate to="/talent/profile" replace />}
          />

          <Route path="/notification" element={<TalentNotification />} />
          <Route path="/talent/profile/edit" element={<TalentEditRedirect />} />
          <Route path="ai-twin-chat/:id" element={<ChatWithAI />} />
          <Route
            path="talent/ai-twin/payment/:talentId"
            element={<AITwinPaymentPage />}
          />
          <Route
            path="talent/ai-twin/enable/:talentId"
            element={<AITwinEnablePage />}
          />
          <Route
            path="talent/ai-twin/conversations/:talentId"
            element={<AITwinConversationList />}
          />
          <Route
            path="talent/ai-twin/conversation/:talentId/:threadId"
            element={<AITwinConversationView />}
          />
          <Route
            path="talent/ai-twin/analytics/:talentId"
            element={<AITwinAnalyticsPage />}
          />
          <Route
            path="talent/ai-twin/monitor/:talentId"
            element={<AITwinMonitorPage />}
          />
          <Route
            path="talent/ai-twin/settings/:talentId"
            element={<AITwinSettingsPage />}
          />
          <Route path="wallet/chips" element={<h1>Talent Chips</h1>} />
          <Route path="wallet/traffic" element={<h1>Talent Wallet Traffic</h1>} />

          <Route path="wallet" element={<TalentWalletDashboardNew />} />

          {/* Airbank */}
          <Route path="/talent/airbank" element={<AirbankPage />} />
          <Route
            path="/talent/airbank/register"
            element={
              <ProtectedRegistrationRoute>
                <AirbankRegistrationPage />
              </ProtectedRegistrationRoute>
            }
          />
          <Route path="/talent/airbank/status" element={<AirbankStatusPage />} />
          <Route path="/talent/airbank/manage" element={<AirbankManagePage />} />
          <Route
            path="/talent/airbank/manage/edit"
            element={<AirbankEditPage />}
          />
          <Route
            path="/talent/airbank/dna-image-usage-logs"
            element={<AirbankDnaImageUsageLogsPage />}
          />

          {/* AI Twins */}
          <Route
            path="/talent/ai-twins"
            element={<AITwinListPage mode="talent" />}
          />
          <Route
            path="/talent/ai-twin/details/:talentId"
            element={<AiTDetailsPage />}
          />

          {/* AiT-enabled brand campaign cards (list) */}
          <Route
            path="/talent/ai-twin-enabled-brand-campaign-cards"
            element={<CardWithTalent_AITwinPage />}
          />
          {/* AiT-enabled brand campaign cards (list + create/edit via modal) */}
          <Route
            path="/talent/create-instant-card-ai-twin"
            element={<InstantCardListTalent />}
          />
          <Route
            path="/talent/edit-instant-card-ai-twin/:id"
            element={<EditAitEnabledCardPage />}
          />
          <Route
            path="/talent/ait-card-requests"
            element={<TalentAitCardRequestsPage />}
          />

          <Route path="/airbank-studio" element={<TalentWorkspacePage />} />

          <Route path="*" element={<div>Route not found</div>} />
        </Route>
      </Routes>
    </Suspense>
  );
};
export default TalentRoutes;
